// Code samples for the integration guide. Plain strings, built from the
// tenant's base URL and the selected key's product so they can be copied as
// they are. No sample ever contains an API key: they read it from the
// CENTRAONE_API_KEY environment variable.

import type { CodeSample } from './guideContent';

export interface SampleContext {
  baseUrl: string;
  /** externalSource to show: the key's product slug or a placeholder. */
  source: string;
  /** True when the key has a product, so externalSource is set by the server. */
  hasProduct: boolean;
  teamId: string;
  externalId: string;
}

function createBody(ctx: SampleContext): Record<string, unknown> {
  return {
    subject: 'Meter read missing for account 44871',
    description: `Details: https://your-system.example/cases/${ctx.externalId}`,
    channel: 'api',
    priority: 'high',
    ...(ctx.hasProduct ? {} : { externalSource: ctx.source }),
    externalId: ctx.externalId,
    teamId: ctx.teamId,
  };
}

function indent(text: string, spaces: number): string {
  const pad = ' '.repeat(spaces);
  return text
    .split('\n')
    .map((line, index) => (index === 0 ? line : pad + line))
    .join('\n');
}

function json(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function csharpObject(value: Record<string, unknown>): string {
  const lines = Object.entries(value).map(([key, val]) => `    ${key} = ${JSON.stringify(val)},`);
  return `new\n{\n${lines.join('\n')}\n}`;
}

export function createTicket(ctx: SampleContext): CodeSample[] {
  const body = createBody(ctx);
  const idempotencyKey = `${ctx.externalId}-create`;
  return [
    {
      lang: 'curl',
      code: `curl -X POST ${ctx.baseUrl}/api/tickets \\
  -H "Authorization: Bearer $CENTRAONE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: ${idempotencyKey}" \\
  -d '${json(body)}'`,
    },
    {
      lang: 'node',
      code: `// Node 18+
const res = await fetch('${ctx.baseUrl}/api/tickets', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.CENTRAONE_API_KEY}\`,
    'Content-Type': 'application/json',
    'Idempotency-Key': '${idempotencyKey}',
  },
  body: JSON.stringify(${indent(json(body), 2)}),
});

const result = await res.json();
if (res.status === 409) {
  // Already created: keep the existing ticket.
  console.log(result.error.existing_ticket.id);
} else if (!res.ok) {
  throw new Error(\`\${result.error.code}: \${result.error.message}\`);
} else {
  console.log(result.id);
}`,
    },
    {
      lang: 'python',
      code: `import os
import requests

res = requests.post(
    "${ctx.baseUrl}/api/tickets",
    headers={
        "Authorization": f"Bearer {os.environ['CENTRAONE_API_KEY']}",
        "Idempotency-Key": "${idempotencyKey}",
    },
    json=${indent(json(body), 4)},
    timeout=30,
)

result = res.json()
if res.status_code == 409:
    # Already created: keep the existing ticket.
    print(result["error"]["existing_ticket"]["id"])
else:
    res.raise_for_status()
    print(result["id"])`,
    },
    {
      lang: 'csharp',
      code: `using System.Net;
using System.Net.Http.Headers;
using System.Net.Http.Json;
using System.Text.Json;

var http = new HttpClient { BaseAddress = new Uri("${ctx.baseUrl}") };
http.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue(
    "Bearer", Environment.GetEnvironmentVariable("CENTRAONE_API_KEY"));

var request = new HttpRequestMessage(HttpMethod.Post, "/api/tickets")
{
    Content = JsonContent.Create(${indent(csharpObject(body), 4)}),
};
request.Headers.Add("Idempotency-Key", "${idempotencyKey}");

var res = await http.SendAsync(request);
var result = await res.Content.ReadFromJsonAsync<JsonElement>();
if (res.StatusCode == HttpStatusCode.Conflict)
{
    // Already created: keep the existing ticket.
    Console.WriteLine(result.GetProperty("error").GetProperty("existing_ticket").GetProperty("id"));
}
else
{
    res.EnsureSuccessStatusCode();
    Console.WriteLine(result.GetProperty("id"));
}`,
    },
  ];
}

export function lookupTicket(ctx: SampleContext): CodeSample[] {
  const query = `externalSource=${ctx.source}&externalId=${ctx.externalId}`;
  return [
    {
      lang: 'curl',
      code: `curl "${ctx.baseUrl}/api/tickets/external?${query}" \\
  -H "Authorization: Bearer $CENTRAONE_API_KEY"`,
    },
    {
      lang: 'node',
      code: `const params = new URLSearchParams({ externalSource: '${ctx.source}', externalId: '${ctx.externalId}' });
const res = await fetch(\`${ctx.baseUrl}/api/tickets/external?\${params}\`, {
  headers: { Authorization: \`Bearer \${process.env.CENTRAONE_API_KEY}\` },
});
const ticket = res.status === 404 ? null : await res.json();`,
    },
    {
      lang: 'python',
      code: `res = requests.get(
    "${ctx.baseUrl}/api/tickets/external",
    headers={"Authorization": f"Bearer {os.environ['CENTRAONE_API_KEY']}"},
    params={"externalSource": "${ctx.source}", "externalId": "${ctx.externalId}"},
    timeout=30,
)
ticket = None if res.status_code == 404 else res.json()`,
    },
    {
      lang: 'csharp',
      code: `var res = await http.GetAsync(
    $"/api/tickets/external?externalSource=${ctx.source}&externalId={Uri.EscapeDataString("${ctx.externalId}")}");
var ticket = res.StatusCode == HttpStatusCode.NotFound
    ? (JsonElement?)null
    : await res.Content.ReadFromJsonAsync<JsonElement>();`,
    },
  ];
}

export function changeStatus(ctx: SampleContext): CodeSample[] {
  return [
    {
      lang: 'curl',
      code: `curl -X PATCH ${ctx.baseUrl}/api/tickets/$TICKET_ID/status \\
  -H "Authorization: Bearer $CENTRAONE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "status": "pending" }'`,
    },
    {
      lang: 'node',
      code: `await fetch(\`${ctx.baseUrl}/api/tickets/\${ticketId}/status\`, {
  method: 'PATCH',
  headers: {
    Authorization: \`Bearer \${process.env.CENTRAONE_API_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ status: 'pending' }),
});`,
    },
    {
      lang: 'python',
      code: `requests.patch(
    f"${ctx.baseUrl}/api/tickets/{ticket_id}/status",
    headers={"Authorization": f"Bearer {os.environ['CENTRAONE_API_KEY']}"},
    json={"status": "pending"},
    timeout=30,
).raise_for_status()`,
    },
    {
      lang: 'csharp',
      code: `var res = await http.PatchAsync(
    $"/api/tickets/{ticketId}/status",
    JsonContent.Create(new { status = "pending" }));
res.EnsureSuccessStatusCode();`,
    },
  ];
}

export function conflictBody(ctx: SampleContext): string {
  return json({
    error: {
      code: 'conflict',
      message: `A ticket with externalSource '${ctx.source}' and externalId '${ctx.externalId}' already exists: TKT-000123 (id 8f0c…)`,
      existing_ticket: { id: '8f0c…', ticket_number: 'TKT-000123' },
      documentation_url: 'https://docs.centraone.ai/api/errors#conflict',
    },
    request_id: '…',
  });
}

export function webhookRegistration(): string {
  return json({
    name: 'Your System receiver',
    url: 'https://your-system.example/hooks/centraone',
    secret: 'a-long-random-string-you-generate',
    events: [
      'ticket.status_changed',
      'ticket.assigned',
      'ticket.resolved',
      'ticket.closed',
      'ticket.reopened',
    ],
  });
}

export function webhookPayload(ctx: SampleContext): string {
  return json({
    id: '…',
    type: 'ticket.resolved',
    api_version: '1.0',
    created_at: '2026-10-13T09:14:02.000Z',
    data: {
      ticketId: '8f0c…',
      ticketNumber: 'TKT-000123',
      subject: 'Meter read missing for account 44871',
      customerId: null,
      origin: ctx.source,
      originType: ctx.hasProduct ? 'product' : 'channel',
      externalSource: ctx.source,
      externalId: ctx.externalId,
      previousStatus: 'in_progress',
      newStatus: 'resolved',
      resolvedAt: '2026-10-13T09:14:02.000Z',
    },
  });
}

export function verifyWebhook(): CodeSample[] {
  return [
    {
      lang: 'node',
      code: `const crypto = require('crypto');

// rawBody: the request body exactly as received (Buffer or string), not re-serialised JSON.
function verify(rawBody, headers, secret) {
  const ts = headers['x-webhook-timestamp-unix'];
  if (!ts || Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false;

  const expected =
    'sha256=' + crypto.createHmac('sha256', secret).update(\`\${ts}.\`).update(rawBody).digest('hex');
  const matches = (got) =>
    typeof got === 'string' &&
    got.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(got), Buffer.from(expected));

  return matches(headers['x-webhook-signature-v2']);
}`,
    },
    {
      lang: 'python',
      code: `import hashlib
import hmac
import time

# raw_body: the request body bytes exactly as received, not re-serialised JSON.
def verify(raw_body: bytes, headers, secret: str) -> bool:
    ts = headers.get("X-Webhook-Timestamp-Unix", "")
    if not ts.isdigit() or abs(time.time() - int(ts)) > 300:
        return False

    mac = hmac.new(secret.encode(), ts.encode() + b"." + raw_body, hashlib.sha256)
    expected = "sha256=" + mac.hexdigest()
    return hmac.compare_digest(expected, headers.get("X-Webhook-Signature-V2", ""))`,
    },
    {
      lang: 'csharp',
      code: `using System.Security.Cryptography;
using System.Text;

// rawBody: the request body bytes exactly as received, not re-serialised JSON.
static bool Verify(byte[] rawBody, IHeaderDictionary headers, string secret)
{
    var ts = headers["X-Webhook-Timestamp-Unix"].ToString();
    if (!long.TryParse(ts, out var sentAt) ||
        Math.Abs(DateTimeOffset.UtcNow.ToUnixTimeSeconds() - sentAt) > 300)
    {
        return false;
    }

    var prefix = Encoding.UTF8.GetBytes(ts + ".");
    var message = new byte[prefix.Length + rawBody.Length];
    Buffer.BlockCopy(prefix, 0, message, 0, prefix.Length);
    Buffer.BlockCopy(rawBody, 0, message, prefix.Length, rawBody.Length);

    using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(secret));
    var expected = "sha256=" + Convert.ToHexString(hmac.ComputeHash(message)).ToLowerInvariant();
    var got = headers["X-Webhook-Signature-V2"].ToString();

    return CryptographicOperations.FixedTimeEquals(
        Encoding.UTF8.GetBytes(expected), Encoding.UTF8.GetBytes(got));
}`,
    },
  ];
}
